import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ConfirmModal, useToast } from '@lms/ui';
import { PERMISSIONS } from '@lms/shared-types';
import { usePermissions } from '@/hooks/usePermissions';
import { api } from '@/lib/api';
import { courseSuccessToast, roleActionErrorToast } from '@/lib/user-feedback';

interface CourseActionsMenuProps {
  courseId: string;
  courseName: string;
  status?: string;
  trashed?: boolean;
  returnQuery?: string;
  variant?: 'dots' | 'gear';
  direction?: 'up' | 'down';
  onSuccess?: () => void;
}

function MenuPanel({
  open,
  direction,
  onClose,
  children,
}: {
  open: boolean;
  direction: 'up' | 'down';
  onClose: () => void;
  children: ReactNode;
}) {
  if (!open) return null;

  return (
    <div
      className={`absolute right-0 z-[120] min-w-[11rem] overflow-hidden rounded-lg border border-[#c6c5d3] bg-white py-1.5 shadow-[0_8px_24px_rgb(0_0_0_/_0.12)] ${
        direction === 'up' ? 'bottom-full mb-1' : 'top-full mt-1'
      }`}
      onClick={onClose}
    >
      {children}
    </div>
  );
}

function MenuLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} className="block w-full px-4 py-2 text-left text-sm text-[#1a1c1e] hover:bg-[#eeeef0] hover:text-[#142175]">
      {children}
    </Link>
  );
}

function MenuButton({
  children,
  onClick,
  danger,
}: {
  children: ReactNode;
  onClick?: () => void;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      className={`block w-full px-4 py-2 text-left text-sm ${
        danger ? 'text-[#ba1a1a] hover:bg-[#ffdad6] hover:text-[#93000a]' : 'text-[#1a1c1e] hover:bg-[#eeeef0] hover:text-[#142175]'
      }`}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

export function CourseActionsMenu({
  courseId,
  courseName,
  status = 'draft',
  trashed = false,
  returnQuery = '',
  variant = 'dots',
  direction = 'up',
  onSuccess,
}: CourseActionsMenuProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const { hasPermission } = usePermissions();
  const canEditCourse = hasPermission(PERMISSIONS.CURSOS_EDITAR);
  const canCreateCourse = hasPermission(PERMISSIONS.CURSOS_CREAR);
  const canDeleteCourse = hasPermission(PERMISSIONS.CURSOS_ELIMINAR);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const previewUrl = `/courses/${courseId}${returnQuery}`;
  const editUrl = `/courses/${courseId}/edit${returnQuery}`;

  useEffect(() => {
    if (!open) return;
    const onClick = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['courses'] });
    onSuccess?.();
  };

  const duplicateCourse = useMutation({
    mutationFn: () => api<{ id: string }>(`/courses/${courseId}/duplicate`, { method: 'POST' }),
    onSuccess: (duplicate) => {
      setOpen(false);
      invalidate();
      navigate(`/courses/${duplicate.id}/edit${returnQuery}`);
    },
  });

  const publishCourse = useMutation({
    mutationFn: () => api(`/courses/${courseId}/publish`, { method: 'POST' }),
    onSuccess: () => {
      setOpen(false);
      invalidate();
      toast(courseSuccessToast('publish'));
    },
    onError: (error) => {
      toast(roleActionErrorToast(error, 'No se pudo publicar el curso'));
    },
  });

  const archiveCourse = useMutation({
    mutationFn: () => api(`/courses/${courseId}/archive`, { method: 'POST' }),
    onSuccess: () => {
      setOpen(false);
      invalidate();
      toast(courseSuccessToast('archive'));
    },
    onError: (error) => {
      toast(roleActionErrorToast(error, 'No se pudo archivar el curso'));
    },
  });

  const removeCourse = useMutation({
    mutationFn: () => api(`/courses/${courseId}`, { method: 'DELETE' }),
    onSuccess: () => {
      setDeleteOpen(false);
      setOpen(false);
      invalidate();
      toast(courseSuccessToast('delete'));
    },
    onError: (error) => {
      setDeleteOpen(false);
      toast(roleActionErrorToast(error, 'No se pudo eliminar el curso'));
    },
  });

  const pending =
    duplicateCourse.isPending || publishCourse.isPending || archiveCourse.isPending || removeCourse.isPending;

  const trigger =
    variant === 'gear' ? (
      <button
        type="button"
        className="moodle-action-icon"
        title="Opciones"
        aria-label={`Opciones de ${courseName}`}
        disabled={pending}
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" aria-hidden="true">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22-.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c-.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z"
          />
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      </button>
    ) : (
      <button
        type="button"
        className="inline-flex h-8 w-8 items-center justify-center rounded-md border-0 bg-transparent p-0"
        title="Opciones"
        aria-label={`Opciones de ${courseName}`}
        disabled={pending}
      >
        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
          <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
        </svg>
      </button>
    );

  return (
    <>
      <div ref={ref} className="relative">
        <div onClick={() => setOpen((value) => !value)}>{trigger}</div>
        <MenuPanel open={open} direction={direction} onClose={() => setOpen(false)}>
          {!trashed ? (
            <>
              <MenuLink to={previewUrl}>Vista previa</MenuLink>
              {canEditCourse && <MenuLink to={editUrl}>Editar</MenuLink>}
              {canCreateCourse && (
                <MenuButton onClick={() => duplicateCourse.mutate()}>Duplicar</MenuButton>
              )}
              {canEditCourse && status !== 'published' && (
                <MenuButton onClick={() => publishCourse.mutate()}>Publicar</MenuButton>
              )}
              {canEditCourse && status !== 'archived' && (
                <MenuButton onClick={() => archiveCourse.mutate()}>Archivar</MenuButton>
              )}
              {canDeleteCourse && (
                <MenuButton danger onClick={() => setDeleteOpen(true)}>
                  Eliminar
                </MenuButton>
              )}
            </>
          ) : canDeleteCourse ? (
            <MenuButton onClick={() => setDeleteOpen(true)}>Eliminar permanentemente</MenuButton>
          ) : null}
        </MenuPanel>
      </div>

      <ConfirmModal
        open={deleteOpen}
        title="Eliminar curso"
        description={`¿Eliminar el curso «${courseName}»? Esta acción no se puede deshacer.`}
        confirmLabel="Eliminar"
        loading={removeCourse.isPending}
        onConfirm={() => removeCourse.mutate()}
        onClose={() => setDeleteOpen(false)}
      />
    </>
  );
}
